import type { Recommendation } from "../../../state/governance";
import { isOpenSuggestion } from "./suggestionsView";

export const SUGGESTION_PAGE_SIZE = 15;
export type SuggestionQueueSort = "priority" | "newest" | "oldest";
const PRIORITY: Readonly<Record<string, number>> = { critical: 4, high: 3, medium: 2, low: 1 };

/** A stable order keeps equal-priority records from moving between pages on refresh. */
export function orderSuggestionQueue(items: ReadonlyArray<Recommendation>, sort: SuggestionQueueSort) {
  const time = (item: Recommendation) => Date.parse(item.createdAt) || 0;
  return [...items].sort((a, b) => {
    if (sort === "priority") {
      const active = Number(isOpenSuggestion(b)) - Number(isOpenSuggestion(a));
      const priority = (PRIORITY[b.severity ?? ""] ?? 0) - (PRIORITY[a.severity ?? ""] ?? 0);
      if (active || priority) return active || priority;
    }
    return (sort === "oldest" ? time(a) - time(b) : time(b) - time(a)) || a.id.localeCompare(b.id);
  });
}

/** Clamp after live removals so the last page never becomes a false empty queue. */
export function suggestionQueuePage<T>(items: ReadonlyArray<T>, requestedPage: number) {
  const pageCount = Math.max(1, Math.ceil(items.length / SUGGESTION_PAGE_SIZE));
  const page = Math.min(Math.max(0, Number.isFinite(requestedPage) ? Math.floor(requestedPage) : 0), pageCount - 1);
  const start = page * SUGGESTION_PAGE_SIZE;
  return {
    page,
    pageCount,
    items: items.slice(start, start + SUGGESTION_PAGE_SIZE),
    from: items.length === 0 ? 0 : start + 1,
    to: Math.min(start + SUGGESTION_PAGE_SIZE, items.length),
    total: items.length,
  };
}

export interface SuggestionDocumentLabel {
  readonly displayName: string;
  readonly filename: string;
}

/** Filename cleanup only; organization names and governed titles are never inferred. */
export const suggestionFilenameLabel = (filename: string): string =>
  filename.replace(/\.pdf$/i, "").replace(/_+/g, " ").replace(/\s+/g, " ").trim();

export function suggestionDocumentLabel(
  recommendation: Pick<Recommendation, "policyId" | "policyName" | "kind">,
  documents?: ReadonlyMap<string, SuggestionDocumentLabel>,
): SuggestionDocumentLabel {
  // Two kinds name no document, for opposite reasons. A missing document is a
  // hole in the library; a company-profile change is not about the library at
  // all, so calling it a library gap would send a reader looking for a file.
  if (recommendation.kind === "org_context") {
    return { displayName: "Company profile", filename: "Settings → Organisation" };
  }
  if (recommendation.policyId === null) return { displayName: "Library gap", filename: "No document in the library" };
  const governed = documents?.get(recommendation.policyId);
  if (governed) return governed;
  const filename = recommendation.policyName?.trim() || "Document";
  return { displayName: suggestionFilenameLabel(filename) || "Document", filename };
}
